import { create } from 'zustand';
import type { ApiError } from './errors.js';
import type { DeepPartial } from './types.js';

export interface ConfigLinks {
  terms?: string;
  privacy?: string;
  support?: string;
  docs?: string;
  status?: string;
  marketing?: string;
  changelog?: string;
  [key: string]: string | undefined;
}

export interface ConfigDefaults {
  timezone: string;
  locale: string;
  currency: string;
  countryIso: string;
}

export interface ConfigHttp {
  timeoutMs: number;
  retry: { attempts: number; baseDelayMs: number; jitter: boolean };
  idempotency: 'auto' | 'off';
  credentials: RequestCredentials;
}

export interface ConfigAuth {
  autoRefreshLeewaySec: number;
  refreshOn401: boolean;
  storageKeys: { access: string; refresh: string };
}

export interface ConfigRealtime {
  autoStart: boolean;
  verbose: boolean;
}

export interface Config {
  apiBaseUrl: string;
  realtimeBaseUrl: string;
  inboxHubPath: string;
  links: ConfigLinks;
  defaults: ConfigDefaults;
  http: ConfigHttp;
  auth: ConfigAuth;
  realtime: ConfigRealtime;
  fetchImpl?: typeof fetch;
  onApiError?: (err: ApiError) => void;
  onAuthExpired?: () => void;
}

export const defaultConfig: Config = {
  apiBaseUrl: 'http://localhost:18080',
  realtimeBaseUrl: 'http://localhost:18080',
  inboxHubPath: '/hubs/inbox',
  links: {},
  defaults: {
    timezone: 'Africa/Johannesburg',
    locale: 'en-ZA',
    currency: 'ZAR',
    countryIso: 'ZA'
  },
  http: {
    timeoutMs: 20_000,
    retry: { attempts: 2, baseDelayMs: 200, jitter: true },
    idempotency: 'auto',
    credentials: 'include'
  },
  auth: {
    autoRefreshLeewaySec: 60,
    refreshOn401: true,
    storageKeys: {
      access: 'reach.auth.accessToken',
      refresh: 'reach.auth.refreshToken'
    }
  },
  realtime: {
    autoStart: true,
    verbose: false
  }
};

let _config: Config = { ...defaultConfig };

function deepMerge<T>(base: T, patch: DeepPartial<T>): T {
  if (patch === undefined || patch === null) return base;
  if (typeof base !== 'object' || base === null) return patch as T;
  if (Array.isArray(base) || Array.isArray(patch)) return (patch ?? base) as T;
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
    const current = (base as Record<string, unknown>)[k];
    if (v !== undefined && typeof v === 'object' && v !== null && !Array.isArray(v) && typeof current === 'object' && current !== null && !Array.isArray(current)) {
      out[k] = deepMerge(current, v as DeepPartial<typeof current>);
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out as T;
}

export const useConfigStore = create<Config>(() => _config);

export function configure(partial: DeepPartial<Config>): Config {
  _config = deepMerge(_config, partial);
  useConfigStore.setState(_config, true);
  return _config;
}

export function getConfig(): Config {
  return _config;
}

export function resetConfig(): void {
  _config = { ...defaultConfig };
  useConfigStore.setState(_config, true);
}
